import { getDoctorOnlineChannels } from '@/common/apis/services/doctor/getDoctorOnlineChannels';
import { useEffect, useMemo, useRef } from 'react';
import { useQuery, useQueryClient, type QueryClient, type UseQueryResult } from '@tanstack/react-query';
import useCustomize from '@/common/hooks/useCustomize';
import { validateDoctorSlug, type DoctorSlugResponse } from '@/common/apis/services/doctor/validateDoctorSlug';
import { getProfile } from '@/modules/profile/functions/getProfileData';
import { samanDoctorAvailabilityStatus } from '@/common/apis/services/booking/samanDoctorAvailabilityStatus';
import {
  resolveDoctorProfileSlugNavigation,
  shouldLookupFullProfileForSlug,
  type DoctorSlugNavigation,
  type FullProfileLookup,
} from '@/modules/profile/utils/resolveDoctorProfileSlugNavigation';
import { assembleProfile, type AggregatorOptions } from '@/modules/profile/functions/assembleProfile';
import * as P from '@/modules/profile/functions/profilePieces';
import { ServerStateKeysEnum } from '@/common/apis/serverStateKeysEnum';
import { CENTERS } from '@/common/types/centers';
import { filterBookableMappedCenterServices, mapDoctorCenterServices } from '@/modules/booking/functions/mapDoctorCenterServices';
import { onlineVisitFirstAvailableQuery } from '@/modules/profile/hooks/useOnlineVisitFirstAvailable';
import { prefetchFactorPayment } from '@/modules/booking/hooks/factor/prefetchFactorPayment';
import { chatLifecycleSlugKey, fetchChatLifecycleAutoCloseHours } from '@/common/apis/services/hami/chatLifecycleSettings';
import { growthbook } from '@/common/growthbook/client';
import { isOnForSlug } from '@/common/growthbook/isOnForSlug';
import { findSearchDoctor, seedPiecesFromSearchDoctor, type SeedPieces } from '@/modules/profile/utils/seedPiecesFromSearch';
import { getProfileSeedFromSearch, type ProfileSeed } from '@/modules/profile/utils/profileSeedFromSearch';
import { reportSlugNavigation, reportSlugValidationError } from '@/modules/profile/utils/reportSlugNavigation';

export type ProgressiveProfile = {
  props: Record<string, any>;
  pieces: P.ProfilePieces;
  seed: ProfileSeed | null;
  /** every applicable piece settled */
  complete: boolean;
  /** slug not found / header data failed with no seed */
  fatal: { statusCode: number } | null;
};

type SlugResult = {
  navigation: DoctorSlugNavigation;
  slugValidation: DoctorSlugResponse | null;
  validationError?: unknown;
};

export const BASE_OPTIONS: AggregatorOptions = {
  useNewDoctorFullNameAPI: true,
  useNewDoctorExpertiseAPI: true,
  useNewDoctorImageAPI: true,
  useNewDoctorBiographyAPI: true,
  useNewDoctorCentersAPI: true,
  useNewDoctorGalleryAPI: true,
  // PROFILE_API is SSR-only (private LAN). Browser always uses legacy.
  useProfileMicroservice: false,
};
const Q = { staleTime: 60_000, retry: 1, refetchOnWindowFocus: false } as const;
const EMPTY_WIDGETS = { widgets: [], widgetsData: {} };

/** react-query v4 rejects `undefined` data; a fetcher resolving `undefined` is a settled `null`. */
const nn =
  <T,>(fn: () => Promise<T>) =>
  async (): Promise<T | null> =>
    (await fn()) ?? null;

type R = Pick<UseQueryResult<any>, 'isSuccess' | 'isError' | 'data'>;
/** success → data, error → `onError`, still loading/disabled → `undefined` (pending). */
function settledValue(r: R, onError: any = null) {
  return r.isSuccess ? r.data : r.isError ? onError : undefined;
}
const isSettled = (r: R) => r.isSuccess || r.isError;

/** Query key of a profile piece. `slug` is the URL slug; slug-keyed pieces add the fetch slug as a dep. */
const pieceKey = (slug: string, university: string | undefined, piece: string, ...deps: unknown[]) => [
  'profilePiece',
  piece,
  slug,
  university ?? null,
  ...deps,
];

/**
 * Online services of the consult center. Also seeds Services' own consult-services query with the same
 * response: otherwise it refetches on mount and shows a 120px placeholder that grows into the
 * online-visit card after reveal (CLS).
 */
const fetchOnlineServicesPiece = (qc: QueryClient, s: string) =>
  P.fetchOnlineServices(s).then(r => {
    if (Array.isArray(r.services)) qc.setQueryData([ServerStateKeysEnum.DoctorCenterServices, s, CENTERS.CONSULT], r.services);
    return r;
  });

/**
 * Starts, at the tap, the requests the booking card waits for (centers, consult services, Saman
 * availability, average waiting time), under the exact keys the profile reads. Next renders the new route in a transition
 * that on a mid-range phone commits ~2s after the tap, and the profile's queries only start then; now
 * they are answered from the cache instead (docs/performance.md). Only pieces whose fetchers send no
 * analytics: the header fetchers (name, expertise, image, biography) report to Splunk and keep their
 * timing. Online-visit pieces (services, first free slot, chat lifecycle) only for online-visit doctors.
 *
 * A prefetch that fails is dropped from the cache unless the profile is already reading it: the
 * profile must start as it would without a prefetch (its own request, search placeholders), not
 * from a cached error — a cached centers/Saman error hid the booking card until the retry, then
 * showed it again (a large layout shift).
 */
export function prefetchBookingPieces(qc: QueryClient, slug: string, university: string | undefined) {
  const key = (piece: string, ...deps: unknown[]) => pieceKey(slug, university, piece, ...deps);
  const opts = { staleTime: Q.staleTime, retry: Q.retry };
  const prefetch = (queryKey: unknown[], fn: () => Promise<unknown>, o: Record<string, unknown>) =>
    qc.prefetchQuery(queryKey, fn, o).then(() => {
      const query = qc.getQueryCache().find(queryKey, { exact: true });
      if (query?.state.status === 'error' && query.getObserversCount() === 0) qc.removeQueries(queryKey, { exact: true });
    });
  const centersDone = prefetch(key('centers', slug), nn(() => P.fetchCenters(slug, university)), opts);
  // The in-person card's "average waiting time" line; without it the card grew 28px after opening.
  void prefetch(key('waiting', slug), nn(() => P.fetchWaitingTime(slug)), opts);
  const consultPieces = () => {
    const onlineDone = prefetch(key('online', slug), () => fetchOnlineServicesPiece(qc, slug), opts);
    // The invoice's phone-call line (doctor's own channels): without it the bubbles grew after showing.
    void prefetch([ServerStateKeysEnum.DoctorOnlineChannels, slug], () => getDoctorOnlineChannels(slug), { staleTime: 60_000, retry: 0 });
    // The online-visit card's "تا N روز پس از ویزیت" line (Hami chat lifecycle), when that flag is on for
    // this doctor: it arrived after the card was on screen and moved it. By slug (no user id yet), the
    // key the profile then seeds its user-id key from (`fallbackSlug`): one request.
    if (isOnForSlug(growthbook, 'online-visit-chat-lifecycle-duration', slug)) {
      void prefetch(chatLifecycleSlugKey(slug), () => fetchChatLifecycleAutoCloseHours({ doctorSlug: slug }), {
        staleTime: 5 * 60 * 1000,
        retry: 1,
      });
    }
    // The online-visit CTA text ("… حدود ساعت ۱۴:۴۵ …") comes from first-available, requested by the card
    // only once it is on screen: its text changed (and could wrap) right after the profile opened.
    // Same services the card shows (Services: bookable 5532 services), same key and fetcher.
    void Promise.all([centersDone, onlineDone]).then(() => {
      const centers = qc.getQueryData<any[] | null>(key('centers', slug));
      const online = qc.getQueryData<{ services: any[] | null }>(key('online', slug));
      const consult = centers?.find?.((c: any) => String(c?.id) === CENTERS.CONSULT);
      if (!consult || !Array.isArray(online?.services)) return;
      filterBookableMappedCenterServices(mapDoctorCenterServices(online!.services, consult.user_center_id)).forEach((service: any) => {
        if (service?.id == null || service?.user_center_id == null) return;
        const q = onlineVisitFirstAvailableQuery(String(service.id), String(service.user_center_id));
        void prefetch(q.queryKey, q.queryFn, { staleTime: q.staleTime, retry: 0 });
        // The invoice's payable amount and payment methods (katibe takes 1–2s): ready before the tap on «ویزیت آنلاین».
        void prefetchFactorPayment(qc, String(service.id), String(service.user_center_id));
      });
    });
  };
  // Online visit: as soon as the search card says so; else once the centers show a 5532 center (some
  // search results list only the in-person centers of an online-visit doctor).
  const searchCenters = seedPiecesFromSearchDoctor(findSearchDoctor(qc, slug)).centers;
  if (searchCenters && P.findConsultCenter({ centers: searchCenters } as P.ProfilePieces)) consultPieces();
  else
    void centersDone.then(() => {
      const centers = qc.getQueryData<any[] | null>(key('centers', slug));
      if (centers?.some?.((c: any) => String(c?.id) === CENTERS.CONSULT)) consultPieces();
    });
  // Same key and fetcher as useSamanDoctorAvailabilityStatus (its staleTime too).
  void prefetch([ServerStateKeysEnum.SamanDoctorAvailabilityStatus, slug], () => samanDoctorAvailabilityStatus(slug), {
    staleTime: 60_000,
  });
}

/** Client slug resolution — same steps as the aggregator (validate → maybe full-profile → resolve). */
async function resolveSlug(slug: string, university: string | undefined): Promise<SlugResult> {
  let slugValidation: DoctorSlugResponse;
  try {
    slugValidation = await validateDoctorSlug(slug);
  } catch (validationError) {
    console.error('Error validating doctor slug:', validationError);
    return {
      navigation: { type: 'render', validatedSlug: slug, slugInfo: null, fullProfileData: null },
      slugValidation: null,
      validationError: validationError ?? 'unknown',
    };
  }
  let fullProfile: FullProfileLookup = null;
  if (shouldLookupFullProfileForSlug(slug, slugValidation)) {
    try {
      const r = await getProfile({ slug, university, isServer: false });
      fullProfile = { data: r.fullProfileData ?? null, redirect: r.redirect };
    } catch {
      fullProfile = null;
    }
  }
  return { navigation: resolveDoctorProfileSlugNavigation({ slug, slugValidation, fullProfile }), slugValidation };
}

export function useProgressiveProfile(slug: string, enabled: boolean): ProgressiveProfile {
  const qc = useQueryClient();
  const university = useCustomize(s => s.customize?.partnerKey) || undefined;
  // Read once per URL slug (not per mount): a client navigation /dr/A → /dr/B keeps this hook mounted,
  // and B must show its own search seed, never A's (A's seed would also mask B's fatal).
  const seedRef = useRef<{ slug: string; seed: ProfileSeed | null; pieces: SeedPieces } | null>(null);
  if (seedRef.current?.slug !== slug) {
    seedRef.current = {
      slug,
      seed: enabled ? getProfileSeedFromSearch(qc, slug) : null,
      pieces: enabled ? seedPiecesFromSearchDoctor(findSearchDoctor(qc, slug)) : {},
    };
  }
  const seed = seedRef.current.seed;
  const seedPieces = seedRef.current.pieces;
  const options: AggregatorOptions = { ...BASE_OPTIONS, fetchExpertiseKeywordTags: true };
  const key = (piece: string, ...deps: unknown[]) => pieceKey(slug, university, piece, ...deps);
  const opts = (on: boolean) => ({ ...Q, enabled: enabled && on });
  // Slug-keyed pieces show the optimistic URL-slug data (same URL slug only) while re-fetching under
  // validatedSlug. Not keepPreviousData: that would also carry doctor A's data into /dr/B on a client
  // navigation (the page stays mounted), revealing A's header under B's URL.
  // Before that, pieces the search result already has are shown from it (stale-while-revalidate):
  // the query still fetches and its fresh response replaces the search data.
  const sopts = (piece: string, on: boolean, fromSearch?: unknown) => ({
    ...opts(on),
    placeholderData: () => (validatedSlug !== slug ? qc.getQueryData(key(piece, slug)) : fromSearch),
  });

  // ---- Tier A: all at once, no dependencies ----
  const nav = useQuery(key('slug'), () => resolveSlug(slug, university), opts(true));
  const navigation = nav.data?.navigation;
  const render = navigation?.type === 'render' ? navigation : null;
  const validatedSlug = render?.validatedSlug ?? slug;
  // Optimistic on the URL slug; once resolution says render under a different canonical slug
  // (case/alias, or full-profile alias with no master user) every slug-keyed piece is re-issued under it.
  const s = validatedSlug;
  const name = useQuery(key('name', s), nn(() => P.fetchFullName(s)), sopts('name', true, seedPieces.fullName));
  const expertise = useQuery(key('expertise', s), nn(() => P.fetchExpertise(s)), sopts('expertise', true, seedPieces.expertise));
  const image = useQuery(key('image', s), nn(() => P.fetchImage(s)), sopts('image', true, seedPieces.image));
  // fetchBiography retries internally; a query retry on top would multiply it.
  const bio = useQuery(key('bio', s), nn(() => P.fetchBiography(s)), { ...sopts('bio', true), retry: 0 });
  const centers = useQuery(key('centers', s), nn(() => P.fetchCenters(s, university)), sopts('centers', true, seedPieces.centers));
  const gallery = useQuery(key('gallery', s), nn(() => P.fetchGallery(s, university)), sopts('gallery', true));
  const rate = useQuery(key('rate', s), nn(() => P.fetchRateDetails(s)), sopts('rate', true, seedPieces.rateDetails));
  const waiting = useQuery(key('waiting', s), nn(() => P.fetchWaitingTime(s)), sopts('waiting', true, seedPieces.waitingTime));
  const pageView = useQuery(key('pv', s), nn(() => P.fetchPageView(s)), sopts('pv', !university, seedPieces.pageView));

  // ---- Tier B: dependency gated ----
  // Pending until slug resolution settles on "render" (redirect / notFound never render pieces).
  const slugInfo = render ? render.slugInfo ?? null : undefined;
  const hideRates = !!(rate.data as any)?.hide_rates;
  // reviews are fetched under the final validatedSlug only (never the optimistic URL slug)
  const reviewsOn = !!render && rate.isSuccess && !rate.isPlaceholderData && !!rate.data && !hideRates;

  const reviews = useQuery(key('reviews', s), nn(() => P.fetchReviews(s)), opts(reviewsOn));
  const code = useQuery(key('code', validatedSlug), nn(() => P.fetchMedicalCode(validatedSlug, slugInfo)), { ...opts(!!render), placeholderData: seedPieces.medicalCode });
  const risman = useQuery(key('risman', slugInfo?.owner_id ?? null, slugInfo?.server_id ?? null), nn(() => P.fetchRisman(slugInfo)), opts(!!render));

  const partial: P.ProfilePieces = {
    validatedSlug,
    slugInfo,
    fullProfile: render ? render.fullProfileData ?? null : undefined,
    rateDetails: settledValue(rate),
    // rejected reviews → {} (legacy); hidden rates / no rate details → null
    reviews: rate.isSuccess && !reviewsOn ? null : rate.isError ? null : settledValue(reviews, {}),
    waitingTime: settledValue(waiting, []),
    risman: settledValue(risman),
    fullName: settledValue(name),
    expertise: settledValue(expertise),
    image: settledValue(image),
    biography: settledValue(bio),
    medicalCode: settledValue(code, ''),
    centers: settledValue(centers),
    gallery: settledValue(gallery),
    pageView: university ? null : settledValue(pageView),
    samanAvailability: null,
  };

  const consult = partial.centers !== undefined ? P.findConsultCenter(partial) : undefined;
  const online = useQuery(
    key('online', s),
    () => fetchOnlineServicesPiece(qc, s),
    { ...opts(!!consult), placeholderData: seedPieces.onlineServices ? { services: seedPieces.onlineServices, channels: [] } : undefined },
  );
  const tagsReady = partial.fullName !== undefined && partial.centers !== undefined;
  const tags = useQuery(key('tags', s), nn(() => P.fetchKeywordTags(s, partial.fullName, partial.centers ?? null)), { ...opts(tagsReady), placeholderData: seedPieces.keywordTags });

  // DrProfile user lookup only when slugInfo carries no user_id (as the aggregator).
  const userOn = !university && !!render && !slugInfo?.user_id;
  const user = useQuery(key('user', s), nn(() => P.fetchUserData(s)), opts(userOn));
  const userData = university || (render && slugInfo?.user_id) ? null : isSettled(user) ? user.data ?? null : undefined;

  const pieces: P.ProfilePieces = {
    ...partial,
    onlineServices:
      partial.centers === undefined ? undefined : !consult ? null : online.isSuccess ? online.data.services : online.isError ? null : undefined,
    onlineChannels: partial.centers === undefined ? undefined : !consult ? [] : online.isSuccess ? online.data.channels : online.isError ? [] : undefined,
    keywordTags: isSettled(tags) ? tags.data ?? [] : undefined,
    userData,
  };

  const queries = [nav, name, expertise, image, bio, centers, gallery, rate, waiting, pageView, reviews, code, risman, online, tags, user];
  const baseDeps = [university, slug, ...queries.flatMap(r => [r.data, r.status, r.fetchStatus])];

  // Pre-assembly: information/centers as the aggregator had them before below-the-fold fetches.
  const pre = useMemo(
    () => assembleProfile(pieces, { university, isServer: false, options }).props,
    // eslint-disable-next-line react-hooks/exhaustive-deps
    baseDeps,
  );

  // User id: effective userData → slugInfo.user_id → information.user_id (aggregator's chain).
  const headerSettled = (['fullName', 'expertise', 'image', 'biography', 'centers'] as const).every(k => pieces[k] !== undefined);
  const belowOn = !university && !!render && userData !== undefined && headerSettled;
  const userId = belowOn
    ? P.resolveEffectiveUserData(pieces)?.user_id ?? slugInfo?.user_id ?? pre.information?.user_id
    : undefined;
  const widgets = useQuery(
    key('widgets', userId ?? null, pre.information?.id ?? null),
    () => P.fetchWidgets(pre.information, P.resolveEffectiveUserData(pieces) ?? { user_id: userId }),
    opts(belowOn),
  );
  const biko = useQuery(key('biko', userId ?? null), nn(() => P.fetchBikoInsurance(userId)), opts(belowOn));
  const facilities = useQuery(key('fac', userId ?? null), nn(() => P.fetchCenterFacilities(userId, pre.centers)), opts(belowOn));

  if (university) {
    Object.assign(pieces, { widgets: EMPTY_WIDGETS, bikoInsurance: null, centerFacilities: {} });
  } else {
    Object.assign(pieces, {
      widgets: settledValue(widgets, EMPTY_WIDGETS),
      bikoInsurance: settledValue(biko),
      centerFacilities: settledValue(facilities, {}),
    });
  }

  // Redirect / 404 — the aggregator's client behaviour. Events once per navigation result
  // (ref guards effect re-runs and StrictMode double effects).
  const reported = useRef<{ slug: string; result: SlugResult } | null>(null);
  const slugResult = nav.data;
  useEffect(() => {
    if (!slugResult) return;
    if (reported.current?.slug === slug && reported.current.result === slugResult) return;
    reported.current = { slug, result: slugResult };
    if (slugResult.slugValidation) {
      reportSlugNavigation(slug, slugResult.navigation, false, slugResult.slugValidation);
    } else if (slugResult.validationError !== undefined) {
      reportSlugValidationError(slug, slugResult.validationError, false);
    }
    if (slugResult.navigation.type === 'redirect') location.replace(encodeURI(slugResult.navigation.destination));
  }, [slug, slugResult]);

  const props = useMemo(
    () => assembleProfile(pieces, { university, isServer: false, options }).props,
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [...baseDeps, ...[widgets, biko, facilities].flatMap(r => [r.data, r.status, r.fetchStatus])],
  );

  // Placeholder (search / optimistic) data never counts: analytics wait for real responses.
  const complete =
    Object.values(pieces).every(v => v !== undefined) &&
    ![name, expertise, image, bio, centers, gallery, rate, waiting, pageView, code, tags, online].some(q => q.isPlaceholderData);
  // Only after slug resolution settled on render: the header queries are then keyed on the final slug,
  // so a failure there is real (never raise it for optimistic URL-slug failures).
  const headFailed = !!render && name.isError && image.isError && expertise.isError;
  const fatal = navigation?.type === 'notFound' ? { statusCode: 404 } : headFailed && !seed ? { statusCode: 500 } : null;
  return { props, pieces, seed, complete, fatal };
}
